import { useEffect, useState } from "react";
import { Router, useLocation, Redirect } from "wouter";
import { ResetPasswordPage } from "../../auth/components/ResetPasswordPage";
import { PublicRouter } from "../../landing/components/PublicRouter";
import { ReleasesPage } from "../../landing/components/ReleasesPage";
import { LandingPage } from "../../landing/components/LandingPage";
import { LegalPage, PrivacyPage } from "../../landing/components/LegalPage";
import { RoadmapPage } from "../../landing/components/RoadmapPage";
import { usePublicTheme } from "../../landing/hooks/usePublicTheme";
import { clearUserCache, hasUserId, setUserCache } from "../../../lib/api/client";
import { syncFromServer } from "../../../lib/api/sync";
import { isKnownAppRoute } from "../../../shared/types/view-routes";
import { NotFoundPage } from "../../landing/components/NotFoundPage";
import { App } from "../../../App";
import { getOAuthCode, clearOAuthUrl } from "../lib/handleOAuth";

const PUBLIC_ROUTES = new Set(["/releases", "/legal", "/privacy", "/roadmap"]);
const DEV_ROUTES = new Set(["/landing"]);

function getResetToken(): string | null {
  const url = new URL(window.location.href);
  if (url.pathname === "/reset-password") {
    return url.searchParams.get("token");
  }
  return null;
}

function PublicPageRouter() {
  const [location] = useLocation();
  const { isDark, toggle } = usePublicTheme();

  if (location === "/releases") {
    return <ReleasesPage isDark={isDark} onToggleTheme={toggle} />;
  }

  if (location === "/legal") {
    return <LegalPage isDark={isDark} onToggleTheme={toggle} />;
  }

  if (location === "/privacy") {
    return <PrivacyPage isDark={isDark} onToggleTheme={toggle} />;
  }

  if (location === "/roadmap") {
    return <RoadmapPage isDark={isDark} onToggleTheme={toggle} />;
  }

  if (location === "/landing") {
    return <LandingPage isDark={isDark} onToggleTheme={toggle} />;
  }

  return null;
}

function AppRouter({ onLogout }: { readonly onLogout: () => void }) {
  const [location] = useLocation();

  useEffect(() => {
    void syncFromServer().then((valid) => {
      if (!valid) {
        clearUserCache();
        onLogout();
      }
    });
  }, [onLogout]);

  if (PUBLIC_ROUTES.has(location) || (import.meta.env.DEV && DEV_ROUTES.has(location))) {
    return <PublicPageRouter />;
  }

  if (!isKnownAppRoute(location)) {
    // Routes auth/publiques avec un user connecté → rediriger vers /today
    if (location === "/login" || location === "/register" || location === "/recover" || location === "/") {
      return <Redirect to="/today" />;
    }
    return <NotFoundPageWithTheme />;
  }

  return <App />;
}

function NotFoundPageWithTheme() {
  const { isDark, toggle } = usePublicTheme();
  return <NotFoundPage isDark={isDark} onToggleTheme={toggle} />;
}

export function RootProvider() {
  const [isLoggedIn, setIsLoggedIn] = useState(() => !getOAuthCode() && hasUserId());
  const [resetToken, setResetToken] = useState(getResetToken);
  const [oauthPending, setOauthPending] = useState(() => !!getOAuthCode());

  useEffect(() => {
    const code = getOAuthCode();
    if (!code) return;

    clearOAuthUrl();

    const apiBase = import.meta.env.VITE_API_URL ?? "/api";
    fetch(`${apiBase}/auth/exchange`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ code }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((user: { id: string; role?: string }) => {
        setUserCache(user.id, user.role);
        setIsLoggedIn(true);
      })
      .catch(() => {
        setIsLoggedIn(false);
      })
      .finally(() => setOauthPending(false));
  }, []);

  if (oauthPending) {
    return null;
  }

  if (resetToken) {
    return (
      <ResetPasswordPage
        token={resetToken}
        onSuccess={() => {
          window.history.replaceState({}, "", "/");
          setResetToken(null);
          setIsLoggedIn(true);
        }}
      />
    );
  }

  if (!isLoggedIn) {
    return (
      <Router>
        <PublicRouter onLogin={() => setIsLoggedIn(true)} />
      </Router>
    );
  }

  return (
    <Router>
      <AppRouter onLogout={() => { window.history.replaceState({}, "", "/"); setIsLoggedIn(false); }} />
    </Router>
  );
}
